import { ROUTINES, type Routine, type RoutineKind } from './routines/library';
import type { Amount, Block, Section, Workout } from './types';

/** Rough seconds per rep when a block is given in reps (for planning only, not for the session timer). */
export const SECONDS_PER_REP = 3;

export function amountSeconds(amount: Amount): number {
  const sides = amount.perSide ? 2 : 1;
  if (amount.kind === 'time') return amount.seconds * sides;
  const reps = amount.repsMax ? (amount.reps + amount.repsMax) / 2 : amount.reps;
  return Math.round(reps * SECONDS_PER_REP * sides);
}

/** The amount a block is done for: its own, or in a circuit the format's work time. */
export function blockAmount(block: Block, section: Section): Amount {
  if (block.amount) return block.amount;
  if (section.format.kind === 'circuit') return { kind: 'time', seconds: section.format.workSec };
  throw new Error(`Block ${block.id} has no amount and its section is not a circuit`);
}

/** Planned length of a section in seconds, including rests. */
export function sectionSeconds(section: Section): number {
  const f = section.format;
  switch (f.kind) {
    case 'list':
      return section.blocks.reduce((sum, b) => sum + amountSeconds(blockAmount(b, section)), 0);
    case 'circuit': {
      // Each block: its work time (format or override) plus restSec, also after a round's last block,
      // plus roundRestSec between rounds. No rest after the very last block: the section ends there
      // (decision 2026-10-13, the section break or the end of the workout replaces it).
      const round = section.blocks.reduce((sum, b) => sum + amountSeconds(blockAmount(b, section)) + f.restSec, 0);
      return f.rounds * round + (f.rounds - 1) * (f.roundRestSec ?? 0) - f.restSec;
    }
    case 'emom':
    case 'amrap':
    case 'continuous':
      return f.minutes * 60;
    case 'sets':
      return section.blocks.reduce((sum, b) => sum + f.sets * amountSeconds(blockAmount(b, section)) + (f.sets - 1) * f.restSec, 0);
  }
}

/** Planned length of a workout with its suggested warm-up and cool-down. */
export function workoutSeconds(workout: Workout): number {
  return withRoutines(workout).sections.reduce((sum, s) => sum + sectionSeconds(s), 0);
}

/** All exercise ids a workout uses (with its suggested warm-up and cool-down), in order, without duplicates. */
export function workoutExerciseIds(workout: Workout): string[] {
  return [...new Set(withRoutines(workout).sections.flatMap((s) => s.blocks.map((b) => b.exerciseId)))];
}

// Warm-up and cool-down routines ------------------------------------------------------------------------

/** Which routine runs instead of the suggested one: a routine id. Missing or unknown: the suggested one. */
export interface RoutineOptions {
  warmupRoutine?: string;
  cooldownRoutine?: string;
}

export function findRoutine(kind: RoutineKind, id: string | null | undefined): Routine | undefined {
  return id ? ROUTINES.find((r) => r.kind === kind && r.id === id) : undefined;
}

/** A routine as a workout section: id and kind "warmup" or "cooldown", each block once. */
export function routineSection(routine: Routine): Section {
  return { id: routine.kind, kind: routine.kind, format: { kind: 'list' }, blocks: routine.blocks };
}

/** Pause between the two halves of a per-side block in the session (DEFAULT_TIMELINE_OPTIONS.switchSideSec). */
const SWITCH_SIDE_SEC = 5;

/** How long a routine runs in the session, side switches included (a test checks it against the timeline). */
export function routineSeconds(routine: Routine): number {
  const section = routineSection(routine);
  const switches = section.blocks.filter((b) => blockAmount(b, section).perSide).length;
  return sectionSeconds(section) + switches * SWITCH_SIDE_SEC;
}

/** The routine a workout's warm-up or cool-down uses: the chosen one, else the suggested one; undefined: its own section (or none). */
export function routineFor(workout: Pick<Workout, 'warmupId' | 'cooldownId'>, kind: RoutineKind, chosen?: string): Routine | undefined {
  return findRoutine(kind, chosen) ?? findRoutine(kind, kind === 'warmup' ? workout.warmupId : workout.cooldownId);
}

/**
 * The workout with its warm-up and cool-down filled in: a routine (the chosen one, else the suggested one) replaces
 * the workout's own sections of that kind, the warm-up first and the cool-down last. Without a routine the own
 * sections stay. Pure; the same options always give the same sections, so a saved session resumes on the same plan.
 */
export function withRoutines(workout: Workout, options: RoutineOptions = {}): Workout {
  let sections = workout.sections;
  for (const kind of ['warmup', 'cooldown'] as const) {
    const routine = routineFor(workout, kind, kind === 'warmup' ? options.warmupRoutine : options.cooldownRoutine);
    if (!routine) continue;
    const rest = sections.filter((s) => s.kind !== kind);
    sections = kind === 'warmup' ? [routineSection(routine), ...rest] : [...rest, routineSection(routine)];
  }
  return sections === workout.sections ? workout : { ...workout, sections };
}

/** Planned seconds for one pass through a section's blocks, without rests (one AMRAP round, one continuous pass). */
export function passSeconds(section: Section): number {
  return section.blocks.reduce((sum, b) => sum + amountSeconds(blockAmount(b, section)), 0);
}

/**
 * Rough number of passes through the blocks that fit in an AMRAP or continuous section, for planning and
 * for a pace hint during the session ("about 6 rounds"). Null for formats with a fixed number of rounds.
 */
export function estimatedPasses(section: Section): number | null {
  const f = section.format;
  if (f.kind !== 'amrap' && f.kind !== 'continuous') return null;
  const pass = passSeconds(section);
  return pass > 0 ? Math.round((f.minutes * 60 / pass) * 10) / 10 : null;
}
